/**
 * WebSocket service for real-time position updates.
 *
 * Features:
 * - Exponential backoff reconnection
 * - Heartbeat handling
 * - AOI and journey subscriptions
 * - Fallback to REST polling on failure
 */

import { API_CONFIG, WS_CONFIG, FRESHNESS } from '../config';
import { useConnectionStore } from '../stores/connectionStore';
import { useSegmentStore } from '../stores/segmentStore';
import { useVehicleStore } from '../stores/vehicleStore';
import type { ViewportResponse } from '../types/api';
import type {
  WSClientMessage,
  WSServerMessage,
  WSPositionUpdate,
  WSPositionsSnapshot,
  WSSegmentUpdate,
} from '../types/websocket';

type MessageHandler = (message: WSServerMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private pendingSubscriptions: WSClientMessage[] = [];
  private stagingActive = false;
  private stagingTimeout: ReturnType<typeof setTimeout> | null = null;
  private stagingRequest: AbortController | null = null;

  /**
   * Connect to the WebSocket server.
   */
  connect(): void {
    if (import.meta.env.VITE_READ_ONLY_STAGING === 'true') {
      if (this.stagingActive) return;
      this.stagingActive = true;
      void this.pollStagingViewport();
      return;
    }
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    // Clean up existing connection
    this.cleanup();

    const { setStatus, setClientId } = useConnectionStore.getState();
    setStatus('connecting');

    try {
      this.ws = new WebSocket(API_CONFIG.WS_BASE);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (error) {
      console.error('WebSocket connection error:', error);
      setStatus('disconnected');
      setClientId(null);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket server.
   */
  disconnect(): void {
    const { reset } = useConnectionStore.getState();
    this.cleanup();
    reset();
  }

  /**
   * Send a message to the server.
   */
  send(message: WSClientMessage): void {
    if (import.meta.env.VITE_READ_ONLY_STAGING === 'true') return;
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      // Queue message for when connection is established
      this.pendingSubscriptions.push(message);
    }
  }

  /**
   * Subscribe to a viewport (map bounding box).
   */
  subscribeViewport(bbox: [number, number, number, number]): void {
    const { setCurrentViewport } = useConnectionStore.getState();
    setCurrentViewport(bbox);

    this.send({
      type: 'subscribe_viewport',
      bbox,
    });
  }

  /**
   * Subscribe to a specific journey.
   */
  subscribeJourney(identifier: string): void {
    this.send({
      type: 'subscribe_journey',
      identifier,
    });
  }

  /**
   * Unsubscribe from all subscriptions.
   */
  unsubscribeAll(): void {
    const { setCurrentViewport } = useConnectionStore.getState();
    setCurrentViewport(null);

    this.send({
      type: 'unsubscribe',
      subscription_id: null,
    });
  }

  /**
   * Add a message handler.
   */
  addMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.add(handler);
  }

  /**
   * Remove a message handler.
   */
  removeMessageHandler(handler: MessageHandler): void {
    this.messageHandlers.delete(handler);
  }

  /**
   * Check if connected.
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // Private methods

  private async pollStagingViewport(): Promise<void> {
    if (!this.stagingActive) return;
    const viewport = useConnectionStore.getState().currentViewport;
    const controller = new AbortController();
    this.stagingRequest = controller;
    try {
      if (viewport) {
        const params = new URLSearchParams({ bbox: viewport.join(','), limit: '800' });
        const response = await fetch(`${API_CONFIG.REST_BASE}/viewport?${params}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Staging viewport returned ${response.status}`);
        const data = await response.json() as ViewportResponse;
        if (!this.stagingActive || controller.signal.aborted) return;
        for (const journey of data.journeys) {
          this.handlePositionUpdate({ ...journey, type: 'position_update', at_stop: false,
            speed_kmh: journey.speed_kmh ?? null, heading: journey.heading ?? null, line: journey.line ?? null });
        }
        const store = useConnectionStore.getState();
        store.setStatus('connected');
        store.setLastHeartbeat(Date.now());
      }
    } catch {
      if (!controller.signal.aborted && this.stagingActive) {
        useConnectionStore.getState().setLastError('Read-only staging refresh failed');
        useConnectionStore.getState().setStatus('disconnected');
      }
    } finally {
      if (this.stagingRequest === controller) this.stagingRequest = null;
      if (this.stagingActive && !controller.signal.aborted) {
        this.stagingTimeout = setTimeout(() => void this.pollStagingViewport(), 5000);
      }
    }
  }

  private handleOpen(): void {
    console.log('WebSocket connected');
    const { setStatus, resetReconnectAttempts } = useConnectionStore.getState();
    setStatus('connected');
    resetReconnectAttempts();

    // Send any pending subscriptions
    while (this.pendingSubscriptions.length > 0) {
      const message = this.pendingSubscriptions.shift();
      if (message) {
        this.send(message);
      }
    }

    // Re-subscribe to current viewport if any
    const { currentViewport } = useConnectionStore.getState();
    if (currentViewport) {
      this.subscribeViewport(currentViewport);
    }

    this.startHeartbeatMonitor();
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message = JSON.parse(event.data) as WSServerMessage;

      // Handle specific message types
      switch (message.type) {
        case 'connection_ack':
          this.handleConnectionAck(message);
          break;

        case 'heartbeat':
          this.handleHeartbeat();
          break;

        case 'position_update':
          this.handlePositionUpdate(message as WSPositionUpdate);
          break;

        case 'positions_snapshot':
          this.handlePositionsSnapshot(message as WSPositionsSnapshot);
          break;

        case 'segment_update':
          this.handleSegmentUpdate(message as WSSegmentUpdate);
          break;

        case 'subscription_ack':
          // Subscription confirmed by server
          break;

        case 'error':
          console.error('WebSocket error:', message.message, message.code);
          useConnectionStore.getState().setLastError(message.message);
          break;
      }

      // Notify all handlers
      this.messageHandlers.forEach((handler) => handler(message));
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  private handleClose(event: CloseEvent): void {
    console.log('WebSocket closed:', event.code, event.reason);
    const { setStatus, setClientId } = useConnectionStore.getState();
    setStatus('disconnected');
    setClientId(null);
    this.cleanup();
    this.scheduleReconnect();
  }

  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    const { setLastError } = useConnectionStore.getState();
    setLastError('WebSocket connection error');
  }

  private handleConnectionAck(message: { client_id: string }): void {
    const { setClientId, setStatus } = useConnectionStore.getState();
    setClientId(message.client_id);
    setStatus('connected');
    console.log('WebSocket client ID:', message.client_id);
  }

  private handleHeartbeat(): void {
    const { setLastHeartbeat } = useConnectionStore.getState();
    setLastHeartbeat(Date.now());

    // Respond with ping
    this.send({ type: 'ping' });

    // Reset heartbeat monitor
    this.startHeartbeatMonitor();
  }

  private handlePositionUpdate(update: Omit<WSPositionUpdate, 'line'> & {
    line: WSPositionUpdate['line'] | null;
  }): void {
    // Drop positions that are already stale on arrival (e.g. old snapshot data)
    if (update.time) {
      const positionAge = Date.now() - new Date(update.time).getTime();
      const maxAge = update.data_source === 'v1-da-gps'
        ? FRESHNESS.V1_ACTIVE_MS
        : FRESHNESS.V4_ACTIVE_MS;
      if (positionAge > maxAge * 3) {
        // Position is 3x past its freshness threshold — discard
        return;
      }
    }

    const { vehicles, setVehicle, setV4Position } = useVehicleStore.getState();

    const existing = vehicles.get(update.service_journey_id);

    // Dual-source handling:
    // - GPS (v1-da-gps) is the primary/preferred source
    // - V4-estimated becomes the "V4 position" when GPS is available
    if (existing) {
      const isIncomingGps = update.data_source === 'v1-da-gps';
      const isExistingGps = existing.data_source === 'v1-da-gps';

      if (isIncomingGps && !isExistingGps) {
        // GPS arriving, vehicle currently has estimated position
        // Save the estimated position as V4 position before updating to GPS
        setV4Position({
          service_journey_id: update.service_journey_id,
          latitude: existing.targetLatitude,
          longitude: existing.targetLongitude,
          data_source: existing.data_source,
          time: existing.time,
        });
      } else if (!isIncomingGps && isExistingGps) {
        // V4-estimated arriving, vehicle already has GPS
        // Always update the V4 secondary position marker
        setV4Position({
          service_journey_id: update.service_journey_id,
          latitude: update.latitude,
          longitude: update.longitude,
          data_source: update.data_source,
          time: update.time,
        });

        // If GPS is still fresh, don't move the main vehicle — GPS takes precedence
        const gpsAge = existing.v1LastUpdateTime
          ? Date.now() - existing.v1LastUpdateTime
          : Infinity;
        if (gpsAge < FRESHNESS.V1_ACTIVE_MS) {
          return;
        }
        // GPS is stale — let V4 update the main vehicle position (fall through)
      }
      // If both are same source, just update normally (fall through)
    }

    setVehicle({
      service_journey_id: update.service_journey_id,
      journey_gid: update.journey_gid,
      latitude: update.latitude,
      longitude: update.longitude,
      speed_kmh: update.speed_kmh,
      heading: update.heading,
      at_stop: update.at_stop,
      data_source: update.data_source,
      line: update.line,
      direction: update.direction ?? null,
      time: update.time,
      polled_at: update.polled_at,
      poller_id: update.poller_id,
    });
  }

  private handlePositionsSnapshot(snapshot: WSPositionsSnapshot): void {
    console.log(`Viewport snapshot: ${snapshot.count} positions`);

    for (const position of snapshot.positions) {
      this.handlePositionUpdate(position);
    }
  }

  private handleSegmentUpdate(message: WSSegmentUpdate): void {
    useSegmentStore.getState().setSegment({
      service_journey_id: message.service_journey_id,
      journey_gid: message.journey_gid,
      state: message.state,
      from_stop: message.from_stop,
      to_stop: message.to_stop,
      segment_geometry: message.segment_geometry,
      time_intervals: message.time_intervals,
      delay_seconds: message.delay_seconds,
      has_realtime: message.has_realtime,
      data_source: message.data_source,
      line_name: message.line_name ?? null,
      transport_mode: message.transport_mode ?? null,
      direction: message.direction ?? null,
      color_bg: message.color_bg ?? null,
    });
  }

  private startHeartbeatMonitor(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
    }

    this.heartbeatTimeout = setTimeout(() => {
      console.warn('Heartbeat timeout - reconnecting');
      this.ws?.close();
    }, WS_CONFIG.HEARTBEAT_TIMEOUT_MS);
  }

  private scheduleReconnect(): void {
    const {
      reconnectAttempts,
      incrementReconnectAttempts,
      setStatus,
      setNextReconnectTime,
    } = useConnectionStore.getState();

    if (reconnectAttempts >= WS_CONFIG.MAX_RECONNECT_ATTEMPTS) {
      console.error('Max reconnection attempts reached - falling back to polling');
      setStatus('disconnected');
      // TODO: Implement REST polling fallback
      return;
    }

    incrementReconnectAttempts();
    setStatus('reconnecting');

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s
    const delay = WS_CONFIG.RECONNECT_DELAY_MS * Math.pow(2, reconnectAttempts);
    const reconnectTime = Date.now() + delay;
    setNextReconnectTime(reconnectTime);

    console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private cleanup(): void {
    this.stagingActive = false;
    this.stagingRequest?.abort();
    this.stagingRequest = null;
    if (this.stagingTimeout) clearTimeout(this.stagingTimeout);
    this.stagingTimeout = null;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;

      if (this.ws.readyState === WebSocket.OPEN) {
        this.ws.close();
      }

      this.ws = null;
    }
  }
}

// Singleton instance
export const websocketService = new WebSocketService();
